import { useSelector } from 'react-redux';
import { IAppState } from '@mafc/common';
import COLORS from 'theme/colors';
import { isExisty } from 'utils/helpers.util';
import { Box, Flex } from '@mafc/trolly';
import TYPOGRAPHY from 'theme/typography';
import React from 'react';
import { Information } from '@mafc/icon';
import { useTranslation } from 'react-i18next';
import useNewC4Journey from 'hooks/useNewC4Journey';
import { getFormattedPrice, getFormattedPriceInUSD, IPrice } from 'utils/price.util';
import PriceInUSD from 'common/PriceInUSD';
import PlaceOrder from './PlaceOrder';
import { CurrencyCodes } from 'pages/Checkout/constants/checkout.constant';

export default ({
  parentRef,
  heading,
  value,
  totalPrice,
  ctaText,
  subHeading,
  callback,
  addOnInfo,
  disabled = false,
  showPrimaryButton = true,
  expressBtn = null,
  paymentMethods = null,
  isCart = false,
  foodNotification = null,
  btnHeight = 42,
  price = '',
}: {
  parentRef?: any;
  heading: string;
  value?: string;
  totalPrice: IPrice;
  ctaText: string;
  subHeading: string;
  addOnInfo: string;
  callback: any;
  disabled?: boolean;
  showPrimaryButton?: boolean;
  expressBtn: any;
  paymentMethods?: any;
  isCart?: boolean;
  foodNotification?: any;
  btnHeight?: number;
  price?: string;
}) => {
  const { t } = useTranslation();
  const isNewJourney = useNewC4Journey();
  const { currencyRate, orderTotalCurrency } = useSelector((state: IAppState) => ({
    currencyRate: state.appConfig && state.appConfig.currencyRate,
    orderTotalCurrency: state.orderSummary && state.orderSummary.orderTotalCurrency,
  }));

  const isUSDSelected = orderTotalCurrency === CurrencyCodes.USD;

  const priceInString = isUSDSelected
    ? getFormattedPriceInUSD(totalPrice, currencyRate)
    : getFormattedPrice(totalPrice, false, true, true);

  return (
    <Box
      ref={parentRef}
      sx={{
        position: ['fixed', null, null, 'static'],
        right: 0,
        left: 0,
        bottom: 0,
        boxShadow: ['0 -1px 8px 0 rgba(0,0,0,0.14)', null, null, 'none'],
        background: COLORS.WHITE,
        zIndex: 3,
        borderRadius: [0, null, null, '8px'],
      }}
      p={3}
    >
      {isCart && foodNotification && isNewJourney ? foodNotification : null}

      {(isExisty(value) || isExisty(price)) && (
        <Flex
          color={COLORS.BLACK}
          alignItems="center"
          justifyContent="space-between"
          mb={isNewJourney ? 0 : 3}
          sx={{
            opacity: disabled ? '0.4' : '1',
            pointerEvents: disabled ? 'none' : 'pointer',
          }}
        >
          {!isNewJourney ? (
            <Flex alignItems={[null, 'center']} fontSize={2}>
              <Box as="span" fontSize={[null, 3]} fontWeight={TYPOGRAPHY.WEIGHT[700]}>
                {heading}
              </Box>
              <Box
                as="span"
                fontSize={[null, TYPOGRAPHY.SIZE[12]]}
                fontWeight={[TYPOGRAPHY.WEIGHT[400], TYPOGRAPHY.WEIGHT[700]]}
                ml={1}
              >
                {subHeading}
              </Box>
            </Flex>
          ) : null}
          <Flex
            flexDirection={['column', 'row', 'column']}
            textAlign={`${isNewJourney ? 'left' : 'right'}`}
            width={`${isNewJourney ? '50%' : 'auto'}`}
          >
            <Box
              as="span"
              fontSize={`${isNewJourney ? [TYPOGRAPHY.SIZE[16], 2] : [TYPOGRAPHY.SIZE[18], 3]}`}
              fontWeight={TYPOGRAPHY.WEIGHT[700]}
              data-testid="__order_summary_cta_value__"
            >
              {priceInString}
            </Box>

            {!isUSDSelected && <PriceInUSD price={totalPrice} variant="cartOrderSummary" />}

            {addOnInfo && (
              <Box
                as="span"
                alignSelf={[null, 'center', 'unset']}
                display={['block', 'inline']}
                fontSize={`${isNewJourney ? [TYPOGRAPHY.SIZE[12]] : 0}`}
                ml={!isNewJourney ? 1 : 0}
              >
                {addOnInfo}
              </Box>
            )}
          </Flex>
          <PlaceOrder
            expressBtn={expressBtn}
            isCart={isCart}
            disabled={disabled}
            btnHeight={btnHeight}
            callback={callback}
            isNewJourney={isNewJourney}
            showPrimaryButton={showPrimaryButton}
            ctaText={ctaText}
          />
          {/* {isNewJourney ? <BtnComponent /> : null} */}
        </Flex>
      )}
      {isCart && !isNewJourney && (
        <Flex mb={3} alignItems="center">
          <Information testid="Information" fill={COLORS.GREY_600} size={'S'} />
          <Box as="span" fontSize={[null, 1]} color={COLORS.GREY_600}>
            {t('deliveryFeesCartMsg')}
          </Box>
        </Flex>
      )}

      {!isNewJourney ? (
        <PlaceOrder
          expressBtn={expressBtn}
          isCart={isCart}
          disabled={disabled}
          btnHeight={btnHeight}
          callback={callback}
          isNewJourney={isNewJourney}
          showPrimaryButton={showPrimaryButton}
          ctaText={ctaText}
        />
      ) : null}
      {isCart && foodNotification && !isNewJourney ? foodNotification : null}
      {!!paymentMethods && paymentMethods}
    </Box>
  );
};
